/**
 * The synchronous cache that removes the first-paint round trip — §11.5,
 * mechanism 2.
 *
 * **Keyed by host, not by tenant ref.** On a shared host two tenants reach the
 * same origin via `?tenant=`, so the key includes the resolved slug when one is
 * present. Keying by tenant ref would be worse than useless: the ref is not
 * known until the payload arrives, which is the very thing being avoided.
 *
 * `localStorage` is the right store here and not for tokens (see `apiClient`),
 * because a logo URL and a hex colour are public — they are on the login page —
 * whereas a bearer token is not.
 *
 * **Every read is defensive.** A corrupt entry, a quota error, or storage being
 * unavailable entirely (Safari private mode, an embedded webview) must degrade
 * to "no cache", never throw: this code runs before React mounts, so an
 * exception here is a blank page rather than a caught error.
 */

import type { BrandingPayload } from '@rd/shared';

/** Bumped when the cached shape changes, so old entries are discarded. */
const CACHE_SCHEMA = 1;

/** A stale brand is a cosmetic error; a very stale one is a support call. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface CacheEntry {
  schema: number;
  payload: BrandingPayload;
  version: number;
  fetchedAt: number;
}

export function cacheKey(host: string, slug?: string | undefined): string {
  return slug ? `branding:${host}:${slug}` : `branding:${host}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readCachedBranding(host: string, slug?: string | undefined): BrandingPayload | null {
  const store = storage();
  if (!store) return null;

  try {
    const raw = store.getItem(cacheKey(host, slug));
    if (!raw) return null;

    const entry = JSON.parse(raw) as Partial<CacheEntry>;
    if (entry.schema !== CACHE_SCHEMA) return null;
    if (typeof entry.fetchedAt !== 'number' || Date.now() - entry.fetchedAt > MAX_AGE_MS) {
      return null;
    }

    const payload = entry.payload;
    // A shape check rather than trust: this value is applied to the DOM before
    // any validation layer has run, and a half-written entry would otherwise
    // throw inside `applyBranding`.
    if (!payload || typeof payload !== 'object') return null;
    if (typeof payload.productName !== 'string') return null;
    if (!payload.colors || typeof payload.colors.primary?.rgb !== 'string') return null;
    if (typeof payload.version !== 'number') return null;

    return payload;
  } catch {
    // Corrupt JSON, a SecurityError, anything at all — silently treat as a
    // cache miss. Never throw, and never leave the bad entry to fail again.
    try {
      store.removeItem(cacheKey(host, slug));
    } catch {
      /* nothing further to try */
    }
    return null;
  }
}

export function writeCachedBranding(
  host: string,
  slug: string | undefined,
  payload: BrandingPayload,
): void {
  const store = storage();
  if (!store) return;

  const entry: CacheEntry = {
    schema: CACHE_SCHEMA,
    payload,
    version: payload.version,
    fetchedAt: Date.now(),
  };

  try {
    store.setItem(cacheKey(host, slug), JSON.stringify(entry));
  } catch {
    // Quota exceeded, or storage disabled. The app works without the cache; it
    // just pays a round trip on the next first paint.
  }
}

export function clearCachedBranding(host: string, slug?: string | undefined): void {
  try {
    storage()?.removeItem(cacheKey(host, slug));
  } catch {
    /* nothing to do */
  }
}

/**
 * The tenant slug from the URL, accepting both spellings.
 *
 * `?tenant=` is what §11.2 step 5 tells customers to send and what the login
 * link carries; `?slug=` is what §11.4's table names. Both work, because a
 * customer following our own documented link must not get an unbranded page.
 */
export function slugFromLocation(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const value = params.get('tenant') ?? params.get('slug');
  if (!value) return undefined;
  const trimmed = value.trim();
  return /^[a-z0-9-]{1,64}$/i.test(trimmed) ? trimmed : undefined;
}
